import { Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { map } from 'rxjs';

import { ApiService } from '@mm-aggregator/drf-dynamics';

import { TimeRangeFilterComponent } from '../../components/time-range-filter/time-range-filter.component';
import { UI, createServerSearch } from '../../components/ui';
import type { EntityConfig } from '../../config/entity-config';
import {
  MessageQueueService,
  type MessageQueueAnalytics,
  type MqBreakdownDimension,
  type MqBreakdownRow,
} from '../../services/message-queue.service';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { HubBarChartComponent } from '../hub/ui/hub-bar-chart.component';
import { HubDoughnutChartComponent } from '../hub/ui/hub-doughnut-chart.component';
import { HubToastService } from '../hub/ui/hub-toast.service';
import {
  DEFAULT_FILTERS,
  formatPercent,
  formatSeconds,
  listQueryFor,
  sortRows,
  toApiFilters,
  type DashboardFilters,
  type SortKey,
} from './message-queues-dashboard.model';

const DIMENSIONS: Array<{ id: MqBreakdownDimension; label: string }> = [
  { id: 'provider', label: 'Providers' },
  { id: 'country', label: 'Countries' },
  { id: 'channel', label: 'Channels' },
  { id: 'merchant', label: 'Merchants' },
  { id: 'application', label: 'Applications' },
];

/** Message queues landing page: filters, KPIs, charts and per provider/country comparisons; list is one click away. */
@Component({
  selector: 'app-message-queues-dashboard',
  standalone: true,
  imports: [FormsModule, RouterLink, TimeRangeFilterComponent, HubBarChartComponent, HubDoughnutChartComponent, ...UI],
  template: `
    <div class="flex w-full flex-col gap-5">
      <header class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 class="m-0 font-heading text-3xl font-extrabold tracking-tight text-fg">Message queues</h1>
          <p class="m-0 mt-1 text-sm text-muted">Delivery health of SMS and email, by period, provider and country.</p>
        </div>
        <a uiButton variant="primary" [routerLink]="listLink()" [queryParams]="listParams()">View all messages</a>
      </header>

      <section class="rounded-xl border border-border bg-card px-5 py-4" aria-label="Filters">
        <div class="flex flex-wrap items-end gap-4">
          <app-time-range-filter (rangeChange)="onRange($event)" />
          <label class="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wider text-muted">
            Channel
            <select uiInput size="sm" [ngModel]="filters().channel" (ngModelChange)="set('channel', $event)">
              <option value="">All</option>
              <option value="SMS">SMS</option>
              <option value="EMAIL">Email</option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wider text-muted">
            Status
            <select uiInput size="sm" [ngModel]="filters().status" (ngModelChange)="set('status', $event)">
              <option value="">All</option>
              @for (s of statuses; track s) { <option [value]="s">{{ s }}</option> }
            </select>
          </label>
          <label class="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wider text-muted">
            Country
            <ui-autocomplete
              class="w-52 font-normal normal-case tracking-normal"
              placeholder="All" ariaLabel="Filter by country"
              [options]="countrySearch.options()" [loading]="countrySearch.loading()" [filterLocal]="false"
              [ngModel]="filters().country" (ngModelChange)="set('country', $event ?? '')"
              (search)="countrySearch.search($event)"
            />
          </label>
          <ui-switch [checked]="filters().compare" (changed)="set('compare', $event)">Compare with previous period</ui-switch>
        </div>
        @if (activeChips().length) {
          <div class="mt-3 flex flex-wrap gap-2">
            @for (chip of activeChips(); track chip.key) {
              <button type="button" class="rounded-full border border-primary bg-primary/10 px-3 py-1 text-xs font-semibold text-fg"
                (click)="set(chip.key, '')" [attr.aria-label]="'Remove filter ' + chip.label">
                {{ chip.label }} ✕
              </button>
            }
          </div>
        }
      </section>

      @if (error()) {
        <ui-alert variant="danger">{{ error() }}</ui-alert>
      }

      @if (loading() && !data()) {
        <div class="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4">
          @for (n of [1, 2, 3, 4, 5, 6]; track n) { <ui-skeleton class="h-24" /> }
        </div>
      } @else if (data(); as d) {
        @if (!d.summary.total) {
          <ui-empty-state title="No messages match these filters." />
        } @else {
          <div class="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4">
            @for (kpi of kpis(); track kpi.label) {
              <ui-stat-card [label]="kpi.label" [value]="kpi.value" [sub]="kpi.sub" />
            }
          </div>

          <div class="grid gap-4 lg:grid-cols-3">
            <section class="rounded-xl border border-border bg-card p-4 lg:col-span-2">
              <h2 class="m-0 mb-2 text-sm font-bold text-fg">Messages over time ({{ d.timeseries.granularity }})</h2>
              <app-hub-bar-chart ariaLabel="Messages over time by status" [stacked]="true"
                [labels]="timeLabels()" [datasets]="timeDatasets()" />
            </section>
            <section class="rounded-xl border border-border bg-card p-4">
              <h2 class="m-0 mb-2 text-sm font-bold text-fg">Status mix</h2>
              <app-hub-doughnut-chart ariaLabel="Status mix" [slices]="statusSlices()" />
            </section>
          </div>

          <div class="grid gap-4 lg:grid-cols-2">
            <section class="rounded-xl border border-border bg-card p-4">
              <h2 class="m-0 mb-2 text-sm font-bold text-fg">Providers: sent vs failed</h2>
              <app-hub-bar-chart ariaLabel="Providers sent vs failed" [stacked]="true"
                [labels]="labelsOf('provider')" [datasets]="sentFailed('provider')" />
            </section>
            <section class="rounded-xl border border-border bg-card p-4">
              <h2 class="m-0 mb-2 text-sm font-bold text-fg">Countries: sent vs failed</h2>
              <app-hub-bar-chart ariaLabel="Countries sent vs failed" [stacked]="true"
                [labels]="labelsOf('country')" [datasets]="sentFailed('country')" />
            </section>
          </div>

          <section class="rounded-xl border border-border bg-card p-4">
            <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h2 class="m-0 text-sm font-bold text-fg">Comparison</h2>
              <ui-tabs [tabs]="dimensionTabs" [(active)]="dimension" />
            </div>
            <div uiTableWrap>
              <table uiTable>
                <thead>
                  <tr>
                    @for (col of columns; track col.key) {
                      <th>
                        <button type="button" class="font-semibold" (click)="sortBy(col.key)">
                          {{ col.label }}{{ sortKey() === col.key ? (sortDir() === 'asc' ? ' ▲' : ' ▼') : '' }}
                        </button>
                      </th>
                    }
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of tableRows(); track row.key + row.name) {
                    <tr>
                      <td>{{ row.name }}</td>
                      <td>{{ row.total }}</td>
                      <td>{{ row.sent }}</td>
                      <td>{{ row.failed }}</td>
                      <td>{{ pct(row.success_rate) }}</td>
                      <td class="whitespace-nowrap text-right">
                        @if (canFilter(row)) {
                          <button type="button" uiButton variant="ghost" size="sm" (click)="drill(row)">Filter</button>
                        }
                        @if (hasListQuery(row)) {
                          <a uiButton variant="ghost" size="sm" [routerLink]="listLink()" [queryParams]="rowListParams(row)">Messages</a>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </section>

          <div class="grid gap-4 lg:grid-cols-2">
            <section class="rounded-xl border border-border bg-card p-4">
              <h2 class="m-0 mb-2 text-sm font-bold text-fg">Top errors</h2>
              @if (d.errors.length) {
                <ul class="m-0 list-none p-0">
                  @for (e of d.errors; track e.error) {
                    <li class="flex justify-between gap-3 border-b border-border py-1.5 text-[0.8125rem]">
                      <span class="min-w-0 break-words text-fg">{{ e.error }}</span>
                      <span class="font-semibold text-fg">{{ e.count }}</span>
                    </li>
                  }
                </ul>
              } @else {
                <p class="text-sm text-muted">No errors in this window.</p>
              }
            </section>
            <section class="rounded-xl border border-border bg-card p-4">
              <h2 class="m-0 mb-2 text-sm font-bold text-fg">Attempts per message</h2>
              <app-hub-bar-chart ariaLabel="Attempts per message"
                [labels]="retryLabels()" [datasets]="retryDatasets()" />
            </section>
          </div>
        }
      }
    </div>
  `,
})
export class MessageQueuesDashboardComponent implements OnInit {
  @Input() entityConfig?: EntityConfig;

  private readonly service = inject(MessageQueueService);
  private readonly api = inject(ApiService);
  private readonly toast = inject(HubToastService);
  private readonly shellRoutes = inject(ShellRoutesService);

  readonly statuses = ['PENDING', 'PROCESSING', 'SENT', 'FAILED', 'RETRYING'];
  readonly dimensionTabs = DIMENSIONS;
  readonly columns: ReadonlyArray<{ key: SortKey; label: string }> = [
    { key: 'name', label: 'Name' },
    { key: 'total', label: 'Total' },
    { key: 'sent', label: 'Sent' },
    { key: 'failed', label: 'Failed' },
    { key: 'success_rate', label: 'Success rate' },
  ];

  readonly filters = signal<DashboardFilters>({ ...DEFAULT_FILTERS });
  readonly data = signal<MessageQueueAnalytics | null>(null);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  /** Server-searched countries (`?search=`): the API returns at most 20 rows. */
  readonly countrySearch = createServerSearch<{ id: string | number; name: string }>({
    fetch: (term) =>
      this.api
        .get<{ results?: Array<{ id: string | number; name: string }> }>('finances/countries/', {
          page: 1,
          search: term,
        })
        .pipe(map((res) => res.results ?? [])),
    toOption: (row) => ({ value: String(row.id), label: row.name }),
    selected: () => (this.filters().country ? [this.filters().country] : []),
  });
  readonly dimension = signal<string>('provider');
  readonly sortKey = signal<SortKey>('total');
  readonly sortDir = signal<'asc' | 'desc'>('desc');
  private requestSeq = 0;

  readonly listLink = computed(() => this.shellRoutes.commands('manage', 'message-queues', 'list'));
  readonly listParams = computed(() => {
    const f = this.filters();
    const params: Record<string, string> = {};
    if (f.channel) params['channel'] = f.channel;
    if (f.status) params['status'] = f.status;
    if (f.country) params['country'] = f.country;
    if (f.provider) params['processor_provider'] = f.provider;
    return params;
  });

  readonly activeChips = computed(() => {
    const f = this.filters();
    const chips: Array<{ key: 'provider' | 'merchant' | 'application'; label: string }> = [];
    if (f.provider) chips.push({ key: 'provider', label: `Provider: ${this.nameOf('provider', f.provider)}` });
    if (f.merchant) chips.push({ key: 'merchant', label: `Merchant: ${this.nameOf('merchant', f.merchant)}` });
    if (f.application) chips.push({ key: 'application', label: `Application: ${this.nameOf('application', f.application)}` });
    return chips;
  });

  readonly kpis = computed(() => {
    const d = this.data();
    if (!d) return [];
    const s = d.summary;
    const delta = (key: string): string => {
      const v = d.previous?.deltas[key];
      return v === null || v === undefined ? '' : `${v > 0 ? '+' : ''}${v}% vs previous`;
    };
    return [
      { label: 'Total', value: s.total, sub: delta('total') },
      { label: 'Sent', value: s.sent, sub: delta('sent') },
      { label: 'Failed', value: s.failed, sub: delta('failed') },
      { label: 'In progress', value: s.pending + s.processing + s.retrying, sub: '' },
      { label: 'Success rate', value: formatPercent(s.success_rate), sub: delta('success_rate') },
      { label: 'Avg attempts', value: s.avg_attempts, sub: delta('avg_attempts') },
      { label: 'Latency p50 / p95', value: `${formatSeconds(s.p50_latency_seconds)} / ${formatSeconds(s.p95_latency_seconds)}`, sub: delta('p95_latency_seconds') },
    ];
  });

  readonly timeLabels = computed(() => {
    const d = this.data();
    if (!d) return [];
    const hourly = d.timeseries.granularity === 'hour';
    return d.timeseries.points.map((p) => {
      const date = new Date(p.bucket);
      return hourly
        ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    });
  });

  readonly timeDatasets = computed(() => {
    const points = this.data()?.timeseries.points ?? [];
    const series: Array<[string, 'sent' | 'failed' | 'retrying' | 'processing' | 'pending', string]> = [
      ['Sent', 'sent', '--c-success'],
      ['Failed', 'failed', '--c-danger'],
      ['Retrying', 'retrying', '--c-warning'],
      ['Processing', 'processing', '--c-info'],
      ['Pending', 'pending', '--c-muted'],
    ];
    return series.map(([label, key, color]) => ({ label, color, data: points.map((p) => p[key]) }));
  });

  readonly statusSlices = computed(() => {
    const s = this.data()?.summary;
    if (!s) return [];
    return [
      { label: 'Sent', value: s.sent, color: '--c-success' },
      { label: 'Failed', value: s.failed, color: '--c-danger' },
      { label: 'Retrying', value: s.retrying, color: '--c-warning' },
      { label: 'Processing', value: s.processing, color: '--c-info' },
      { label: 'Pending', value: s.pending, color: '--c-muted' },
    ].filter((slice) => slice.value > 0);
  });

  readonly retryLabels = computed(() => (this.data()?.retries ?? []).map((r) => `${r.attempts}`));
  readonly retryDatasets = computed(() => [
    { label: 'Messages', color: '--c-primary', data: (this.data()?.retries ?? []).map((r) => r.count) },
  ]);

  readonly tableRows = computed(() => {
    const rows = this.data()?.breakdowns[this.dimension() as MqBreakdownDimension] ?? [];
    return sortRows(rows, this.sortKey(), this.sortDir());
  });

  ngOnInit(): void {
    this.countrySearch.search('');
    this.load();
  }

  pct = formatPercent;

  onRange(range: { period?: string; since?: string; until?: string }): void {
    this.filters.update((f) => ({ ...f, period: range.period, since: range.since, until: range.until }));
    this.load();
  }

  set<K extends keyof DashboardFilters>(key: K, value: DashboardFilters[K]): void {
    this.filters.update((f) => ({ ...f, [key]: value }));
    this.load();
  }

  sortBy(key: SortKey): void {
    if (this.sortKey() === key) {
      this.sortDir.update((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      this.sortKey.set(key);
      this.sortDir.set(key === 'name' ? 'asc' : 'desc');
    }
  }

  labelsOf(dimension: MqBreakdownDimension): string[] {
    return (this.data()?.breakdowns[dimension] ?? []).slice(0, 12).map((r) => r.name);
  }

  sentFailed(dimension: MqBreakdownDimension): Array<{ label: string; color: string; data: number[] }> {
    const rows = (this.data()?.breakdowns[dimension] ?? []).slice(0, 12);
    return [
      { label: 'Sent', color: '--c-success', data: rows.map((r) => r.sent) },
      { label: 'Failed', color: '--c-danger', data: rows.map((r) => r.failed) },
    ];
  }

  canFilter(row: MqBreakdownRow): boolean {
    return !!row.id && this.dimension() !== 'channel';
  }

  hasListQuery(row: MqBreakdownRow): boolean {
    return Object.keys(listQueryFor(this.dimension() as MqBreakdownDimension, row)).length > 0;
  }

  rowListParams(row: MqBreakdownRow): Record<string, string> {
    return listQueryFor(this.dimension() as MqBreakdownDimension, row);
  }

  drill(row: MqBreakdownRow): void {
    if (!row.id) return;
    const dim = this.dimension();
    const key = dim === 'provider' ? 'provider' : dim === 'country' ? 'country' : dim === 'merchant' ? 'merchant' : 'application';
    this.set(key, row.id);
  }

  private nameOf(dimension: MqBreakdownDimension, id: string): string {
    return this.data()?.breakdowns[dimension].find((r) => r.id === id)?.name ?? id.slice(0, 8);
  }

  private load(): void {
    const seq = ++this.requestSeq;
    this.loading.set(true);
    this.error.set(null);
    this.service.getAnalytics(toApiFilters(this.filters())).subscribe({
      next: (res) => {
        if (seq !== this.requestSeq) return;
        this.data.set(res);
        this.loading.set(false);
      },
      error: () => {
        if (seq !== this.requestSeq) return;
        this.loading.set(false);
        this.error.set('Could not load message analytics.');
        this.toast.error('Could not load message analytics.');
      },
    });
  }
}
